import type { PlantContract, RevenueDisplayType } from '@/shared/data-types';

// 사업구분 상태(displayType) 판별 + 파생 helper — 구앱 `plant.utils` 이식. 메트릭 라벨·완료 여부·고정단가·계약상세를
// 좌우한다. 수익(widgets/revenue)·발전소 정보(widgets/plant-info)·대시보드가 공유하는 계약 표현 SSOT → shared/lib.

const isAbsent = (value: string | null): boolean => value == null || value === '';

/**
 * 계약 상세 접근 — 매퍼(`toContractDetail`)가 이미 object 로 정규화해 두므로 여기선 파싱하지 않는다.
 * **키 구성은 계약코드마다 다르다**(실측: 고정단가 P101/P103 = ctrtStDate·ctrtEdDate·recRate·unitPrice /
 * 현물 P102 = recRate 만 / 자가소비 F2xx = contractDetail 키 자체 없음) — 그래서 아래 접근자들이 키 단위로 방어한다.
 */
function contractDetailOf(contract: PlantContract | null): Record<string, unknown> | null {
  return contract?.contractDetail ?? null;
}

/** 계약 → displayType(6종). 없음=미등록 / 02=자가소비(계약종별 유무) / 01=발전사업(미설정·현물·고정단가). */
export function getRevenueDisplayType(contract: PlantContract | null): RevenueDisplayType {
  if (!contract) return 'NOT_REGISTERED';
  const { useCode, contractCode } = contract;
  const isFixedPrice = contractCode === 'P101' || contractCode === 'P103';
  if (useCode === '02') {
    return isAbsent(contractCode) ? 'SELF_NOT_SET' : 'SELF_SET';
  }
  // 발전사업(01)
  if (isAbsent(contractCode)) return 'SALE_NOT_SET';
  return isFixedPrice ? 'SALE_FIXED_SET' : 'SALE_SET';
}

/** 메트릭 값 어휘 — 자가소비=요금편익 / 그 외=추정수익. */
export function getRevenueLabel(type: RevenueDisplayType): 'benefit' | 'revenue' {
  return type === 'SELF_NOT_SET' || type === 'SELF_SET' ? 'benefit' : 'revenue';
}

/** 수익 정보 완료 여부 — 설정 완료(SELF_SET·SALE_SET·SALE_FIXED_SET)면 true(값 정상 표기·클릭 없음). */
export function isRevenueInfoComplete(type: RevenueDisplayType): boolean {
  return type === 'SELF_SET' || type === 'SALE_SET' || type === 'SALE_FIXED_SET';
}

/** 발전사업(01) 계열 여부(SALE_*) — 단일 mode 파생·행 클릭 상세시트 게이트의 계약 기반 SSOT. NOT_REGISTERED·SELF_* 제외. */
export function isSaleType(type: RevenueDisplayType | null): boolean {
  return type === 'SALE_NOT_SET' || type === 'SALE_SET' || type === 'SALE_FIXED_SET';
}

/** 계약종별(contractCode) 존재 = 완료(전체 스코프 완료 판정용). */
export function hasCompleteContractInfo(contract: PlantContract | null): boolean {
  return contract != null && !isAbsent(contract.contractCode);
}

/** 고정단가(P101/P103) 단가 — contractDetail `{ unitPrice }`. 그 외 계약코드·키 결측 = 0. */
export function getFixedUnitPrice(contract: PlantContract | null): number {
  if (!contract || (contract.contractCode !== 'P101' && contract.contractCode !== 'P103')) return 0;
  const detail = contractDetailOf(contract);
  return typeof detail?.unitPrice === 'number' ? detail.unitPrice : 0;
}

/** yyyymmdd(8자리) → 'yyyy.mm.dd'. 8자리 아니면 null(구앱 formatContractDate 정합). */
function formatContractDate(value: unknown): string | null {
  if (typeof value !== 'string' || value.length !== 8) return null;
  return `${value.slice(0, 4)}.${value.slice(4, 6)}.${value.slice(6, 8)}`;
}

/**
 * 고정단가(P101/P103) 계약기간 — contractDetail `{ ctrtStDate, ctrtEdDate }`(yyyymmdd)를
 * 'yyyy.mm.dd ~ yyyy.mm.dd' 로. 키 결측(현물·자가소비) = null(정보상세 계약 섹션 전용, 구앱 renderFixedPriceDetails 정합).
 */
export function getContractPeriod(contract: PlantContract | null): string | null {
  const detail = contractDetailOf(contract);
  const start = formatContractDate(detail?.ctrtStDate);
  const end = formatContractDate(detail?.ctrtEdDate);
  return start && end ? `${start} ~ ${end}` : null;
}

/** REC 가중치 — contractDetail `{ recRate }`(현물 P102·고정단가 모두 보유). 숫자 아니면 null(포맷 없이 원값). */
export function getRecRate(contract: PlantContract | null): number | null {
  const detail = contractDetailOf(contract);
  return typeof detail?.recRate === 'number' ? detail.recRate : null;
}
